import { parseBoard, parseRack, playFromTiles, type Board } from "./board";
import {
  BLANK,
  EMPTY,
  RACK_MAX,
  SIZE,
  cellKey,
  type PlacedTile,
  type ScrabbleDefinition,
  type ScrabbleProgress,
  type ScrabbleSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Place one word on the board using tiles from your rack, across or down. It must touch a tile already on the board (or cover the centre star if the board is empty), and every word it forms must be in the dictionary. Premium squares count only under the tiles you place; a blank is worth nothing; using all seven tiles earns 50 extra points. Your first valid play is final: you are ranked on its points.",
  fr: "Posez un mot sur la grille avec les lettres de votre chevalet, horizontalement ou verticalement. Il doit toucher une lettre déjà posée (ou couvrir l'étoile centrale si la grille est vide), et chaque mot formé doit être dans le dictionnaire. Les cases primes ne comptent que sous les lettres posées ; un joker ne vaut rien ; poser ses sept lettres rapporte 50 points de plus. Votre premier coup valide est définitif : vous êtes classé sur ses points.",
};

/** The same starting rack as the backend's `empty_definition()`. */
export const STARTER_RACK = "AEINRST";

export function emptyDefinition(): ScrabbleDefinition {
  return {
    puzzleType: "scrabble",
    typeVersion: 1,
    language: "en",
    board: Array.from({ length: SIZE }, () => EMPTY.repeat(SIZE)),
    rack: STARTER_RACK,
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: ScrabbleDefinition): ScrabbleProgress {
  return { version: 1, placed: [] };
}

function isTile(raw: unknown): raw is PlacedTile {
  if (typeof raw !== "object" || raw === null) return false;
  const t = raw as Record<string, unknown>;
  return (
    Number.isInteger(t.r) &&
    Number.isInteger(t.c) &&
    typeof t.letter === "string" &&
    /^[A-Z]$/.test(t.letter) &&
    typeof t.blank === "boolean"
  );
}

/**
 * Keeps only the tiles this board and rack can actually hold: on an empty cell, one per
 * cell, servable by the rack, at most a rackful. Anything else — an older shape, a blob
 * recorded against another definition, garbage — is dropped rather than drawn.
 */
export function sanitizePlaced(definition: ScrabbleDefinition, raw: unknown): PlacedTile[] {
  if (!Array.isArray(raw)) return [];
  let board: Board;
  let rack: Map<string, number>;
  try {
    board = parseBoard(definition.board);
    rack = new Map(parseRack(definition.rack));
  } catch {
    return [];
  }
  const kept: PlacedTile[] = [];
  const taken = new Set<string>();
  for (const item of raw) {
    if (!isTile(item) || kept.length >= RACK_MAX) continue;
    if (item.r < 0 || item.r >= SIZE || item.c < 0 || item.c >= SIZE) continue;
    const key = cellKey([item.r, item.c]);
    if (board.has(key) || taken.has(key)) continue;
    const need = item.blank ? BLANK : item.letter;
    const left = rack.get(need) ?? 0;
    if (left === 0) continue;
    rack.set(need, left - 1);
    taken.add(key);
    kept.push({ r: item.r, c: item.c, letter: item.letter, blank: item.blank });
  }
  return kept;
}

export function migrateProgress(definition: ScrabbleDefinition, raw: unknown): ScrabbleProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  return { version: 1, placed: sanitizePlaced(definition, (raw as { placed?: unknown }).placed) };
}

/** The play the tiles spell; tiles that spell none are sent as an empty word the server refuses. */
export function serializeSubmission(
  definition: ScrabbleDefinition,
  progress: ScrabbleProgress,
): ScrabbleSubmission {
  try {
    const play = playFromTiles(parseBoard(definition.board), progress.placed);
    if (play) return play;
  } catch {
    // An unreadable board cannot be played on; fall through to the refusal below.
  }
  return { row: 0, col: 0, direction: "across", word: "" };
}
